<template>
  <div class="act004-progress-bar" :style="hostStyle">
    <img
      v-if="bgSrc"
      class="act004-progress-bar__bg"
      :src="bgSrc"
      alt=""
      @load="measureTrack"
    />
    <div ref="trackEl" class="act004-progress-bar__track" :style="trackStyle">
      <div
        v-if="fillSrc && totalTileCount > 0"
        class="act004-progress-bar__fill-clip"
        :style="fillClipStyle"
      >
        <div class="act004-progress-bar__fill-tiles">
          <img
            v-for="ti in totalTileCount"
            :key="'pb-tile-' + ti"
            class="act004-progress-bar__tile"
            :src="fillSrc"
            :style="tileStyle"
            alt=""
            @load="onTileImgLoad"
          />
        </div>
      </div>
      <img
        v-for="(seg, si) in visibleSegments"
        :key="'pb-seg-' + si"
        class="act004-progress-bar__segment"
        :src="seg.src"
        :style="seg.style"
        alt=""
      />
    </div>
  </div>
</template>

<script>
import { rpxToPx } from '@/utils'

export default {
  name: 'Act004ProgressBarLayer',
  props: {
    bgSrc: { type: String, default: '' },
    fillSrc: { type: String, default: '' },
    segments: { type: Array, default: () => [] },
    progressPercent: { type: Number, default: 0 },
    /** 设计稿 rpx */
    fillTileWidthRpx: { type: Number, default: 14 },
    fillTileHeightRpx: { type: Number, default: 32 },
    fillTileGapRpx: { type: Number, default: 5 },
    windowWidthPx: { type: Number, default: 375 },
    trackInsetLeft: { type: Number, default: 5 },
    trackInsetTop: { type: Number, default: 22 },
    trackInsetRight: { type: Number, default: 5 },
    trackInsetBottom: { type: Number, default: 22 }
  },
  data() {
    return {
      trackWidthPx: 0
    }
  },
  computed: {
    hostStyle() {
      return { pointerEvents: 'none' }
    },
    trackStyle() {
      return {
        left: this.trackInsetLeft + '%',
        top: this.trackInsetTop + '%',
        right: this.trackInsetRight + '%',
        bottom: this.trackInsetBottom + '%'
      }
    },
    progressClamped() {
      return Math.max(0, Math.min(100, this.progressPercent))
    },
    tileWidthPx() {
      const w = Number(this.fillTileWidthRpx)
      const rpx = Number.isFinite(w) && w > 0 ? w : 14
      return parseFloat(rpxToPx(rpx, this.windowWidthPx)) || 1
    },
    tileHeightPx() {
      const h = Number(this.fillTileHeightRpx)
      const rpx = Number.isFinite(h) && h > 0 ? h : 32
      return parseFloat(rpxToPx(rpx, this.windowWidthPx)) || 1
    },
    tileGapPx() {
      const g = Number(this.fillTileGapRpx)
      const rpx = Number.isFinite(g) && g >= 0 ? g : 5
      return parseFloat(rpxToPx(rpx, this.windowWidthPx)) || 0
    },
    tileStepPx() {
      return this.tileWidthPx + this.tileGapPx
    },
    totalTileCount() {
      if (!this.fillSrc || this.trackWidthPx <= 0 || this.tileStepPx <= 0) return 0
      return Math.ceil(this.trackWidthPx / this.tileStepPx) + 2
    },
    fillClipStyle() {
      const trackW = this.trackWidthPx
      if (trackW <= 0) return { width: '0px' }
      const clipW = (this.progressClamped / 100) * trackW
      return {
        width: Math.max(0, clipW) + 'px'
      }
    },
    tileStyle() {
      return {
        width: this.tileWidthPx + 'px',
        height: this.tileHeightPx + 'px',
        marginRight: this.tileGapPx + 'px',
        flex: '0 0 auto'
      }
    },
    /** 仅渲染配置了独立 blockImage 的分段；重复填充由 fillTiles 负责 */
    visibleSegments() {
      const p = this.progressClamped
      return (this.segments || [])
        .map((seg) => {
          const src = String(seg.blockImage || '').trim()
          if (!src) return null
          const start = parseFloat(seg.startPercent)
          const end = parseFloat(seg.endPercent)
          if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null
          if (p < start) return null
          return {
            style: {
              left: start + '%',
              width: end - start + '%'
            },
            src
          }
        })
        .filter(Boolean)
    }
  },
  watch: {
    trackInsetLeft: 'measureTrack',
    trackInsetTop: 'measureTrack',
    trackInsetRight: 'measureTrack',
    trackInsetBottom: 'measureTrack',
    fillTileWidthRpx: 'measureTrack',
    fillTileGapRpx: 'measureTrack',
    windowWidthPx: 'measureTrack',
    progressPercent: 'measureTrack',
    fillSrc: 'measureTrack'
  },
  mounted() {
    this.measureTrack()
    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(() => this.measureTrack())
      this.$nextTick(() => {
        if (this.$refs.trackEl) this._ro.observe(this.$refs.trackEl)
      })
    }
    this._measureRetryTimer = setTimeout(() => this.measureTrack(), 80)
    this._measureRetryTimer2 = setTimeout(() => this.measureTrack(), 300)
  },
  beforeDestroy() {
    if (this._ro) {
      this._ro.disconnect()
      this._ro = null
    }
    if (this._measureRetryTimer) clearTimeout(this._measureRetryTimer)
    if (this._measureRetryTimer2) clearTimeout(this._measureRetryTimer2)
  },
  updated() {
    this.measureTrack()
  },
  methods: {
    onTileImgLoad() {
      this.measureTrack()
    },
    measureTrack() {
      this.$nextTick(() => {
        const el = this.$refs.trackEl
        if (!el) return
        const w = el.clientWidth || 0
        if (w !== this.trackWidthPx) {
          this.trackWidthPx = w
        }
      })
    }
  }
}
</script>

<style scoped>
.act004-progress-bar {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.act004-progress-bar__bg {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: fill;
  display: block;
}
.act004-progress-bar__track {
  position: absolute;
  overflow: hidden;
}
.act004-progress-bar__fill-clip {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  height: auto;
  max-height: 100%;
  overflow: hidden;
  will-change: width;
}
.act004-progress-bar__fill-tiles {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: nowrap;
  width: max-content;
}
.act004-progress-bar__tile {
  display: block;
  object-fit: fill;
  pointer-events: none;
}
.act004-progress-bar__segment {
  position: absolute;
  top: 0;
  height: 100%;
  object-fit: fill;
  display: block;
  pointer-events: none;
}
</style>
